import { SITE_CONFIG } from '../config/siteConfig';

/**
 * Paths served by peppar-site (Next.js) in production via Nginx.
 * The React SPA must not client-route these — use full page loads instead.
 */
const MARKETING_EXACT_PATHS = new Set([
  '/',
  '/about',
  '/contact',
  '/help',
  '/faqs',
  '/blog',
  '/pricing',
  '/privacy-policy',
  '/privacy-and-policy',
  '/terms-and-conditions',
]);

const MARKETING_PREFIXES = [
  '/blog/',
  '/learn/',
  '/pos-for-',
  '/restaurant-pos-',
  '/peppar-vs-',
  '/restaurant-pos',
  '/cloud-kitchen-pos',
  '/qr-menu',
  '/digital-menu',
  '/restaurant-billing-software',
  '/inventory-management-for-restaurants',
  '/kitchen-display-system',
  '/digital-signage',
  '/split-bill-software',
  '/restaurant-in-app-games',
  '/restaurant-crm',
  '/restaurant-loyalty-program',
  '/restaurant-reporting-software',
  '/virtual-captain',
  '/ai-for-restaurants',
  '/restaurant-ai-assistant',
  '/digital-waiter',
  '/restaurant-ai-ordering',
  '/best-restaurant-pos-in-india',
  '/best-cloud-kitchen-software',
  '/best-digital-menu-platform',
  '/direct-delivery',
  '/peppar-rider',
  '/dine-in-ordering',
  '/takeaway-ordering',
  '/table-reservation',
  '/integrations',
];

/** App routes owned by the React SPA (Tomcat). */
const REACT_APP_PREFIXES = [
  '/signin',
  '/forgotPassword',
  '/resetPassword',
  '/partner/',
  '/supplier/',
  '/restaurants/',
];

function normalizePath(path: string): string {
  const raw = (path || '/').split('?')[0].split('#')[0] || '/';
  if (raw.length > 1 && raw.endsWith('/')) {
    return raw.slice(0, -1);
  }
  return raw;
}

export function isMarketingSitePath(path: string): boolean {
  const pathname = normalizePath(path);
  if (REACT_APP_PREFIXES.some((p) => pathname === p.replace(/\/$/, '') || pathname.startsWith(p))) {
    return false;
  }
  if (pathname === '/restaurants') {
    return false;
  }
  if (MARKETING_EXACT_PATHS.has(pathname)) {
    return true;
  }
  return MARKETING_PREFIXES.some((prefix) => pathname.startsWith(prefix) || pathname === prefix.replace(/\/$/, ''));
}

/**
 * Base URL for the marketing site. Empty string = same origin (production behind Nginx).
 * Local Tomcat dev defaults to peppar-site on :3001.
 */
export function getMarketingSiteBase(): string {
  const fromEnv = process.env.REACT_APP_MARKETING_SITE_URL?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3001';
    }
  }
  return SITE_CONFIG.urls.site.replace(/\/$/, '');
}

/** Build href for a marketing page (full navigation — not React Router). */
export function getMarketingSiteUrl(path: string): string {
  const base = getMarketingSiteBase();
  if (!path) {
    return base || '/';
  }
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (path.startsWith('#')) {
    return base ? `${base}/${path}` : `/${path}`;
  }
  if (path.startsWith('/#')) {
    return base ? `${base}${path}` : path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (!base || (typeof window !== 'undefined' && base === window.location.origin)) {
    return normalized;
  }
  return `${base}${normalized}`;
}

export function navigateToMarketingSite(path: string): void {
  window.location.assign(getMarketingSiteUrl(path));
}
